"use client";

import { useId } from "react";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/**
 * A free-body diagram: the object as a box, each force as an arrow from its centre, and
 * the net force as a dashed arrow. Arrow lengths share one scale. All text arrives
 * through props; colours are attributes so the picture survives a PNG export.
 */
export interface DiagramForce {
  magnitude: number;
  /** Radians counter-clockwise from the +x axis. */
  angle: number;
  label: string;
}

export interface DiagramLabels {
  title: string;
  net: string;
}

const SIZE = 320;
const CENTRE = SIZE / 2;
const MAX_LENGTH = 100;
const BOX = 18;
const LABEL_REACH = 14;
const LABEL_MARGIN = 28;
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;
const FONT = "system-ui, -apple-system, Segoe UI, sans-serif";

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

function tip(magnitude: number, angle: number, scale: number) {
  return {
    x: CENTRE + magnitude * scale * Math.cos(angle),
    y: CENTRE - magnitude * scale * Math.sin(angle),
  };
}

export function FreeBodyDiagram({
  forces,
  net,
  labels,
}: {
  forces: readonly DiagramForce[];
  net: { magnitude: number; angle: number | null };
  labels: DiagramLabels;
}) {
  const uid = useId().replace(UNSAFE_ID, "");
  const longest = Math.max(
    net.magnitude,
    ...forces.map((f) => f.magnitude),
    1e-12
  );
  const scale = MAX_LENGTH / longest;
  const arrowId = (index: number) => `fbd-arrow-${uid}-${index}`;
  const netId = `fbd-net-${uid}`;
  return (
    <svg
      aria-label={labels.title}
      className="h-auto w-full"
      fontFamily={FONT}
      role="img"
      style={{ maxWidth: "20rem" }}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{labels.title}</title>
      <rect fill={CHART_INK.background} height={SIZE} width={SIZE} />
      <defs>
        {forces.map((_, index) => (
          <marker
            id={arrowId(index)}
            key={arrowId(index)}
            markerHeight="7"
            markerWidth="7"
            orient="auto"
            refX="6"
            refY="3.5"
          >
            <path d="M0 0 L7 3.5 L0 7 Z" fill={CHART_COLORS[index % 4]} />
          </marker>
        ))}
        <marker
          id={netId}
          markerHeight="7"
          markerWidth="7"
          orient="auto"
          refX="6"
          refY="3.5"
        >
          <path d="M0 0 L7 3.5 L0 7 Z" fill={CHART_COLORS[7]} />
        </marker>
      </defs>
      <line
        stroke={CHART_INK.grid}
        x1={8}
        x2={SIZE - 8}
        y1={CENTRE}
        y2={CENTRE}
      />
      <line
        stroke={CHART_INK.grid}
        x1={CENTRE}
        x2={CENTRE}
        y1={8}
        y2={SIZE - 8}
      />
      {forces.map((force, index) => {
        if (!(force.magnitude > 0)) {
          return null;
        }
        const end = tip(force.magnitude, force.angle, scale);
        const label = tip(force.magnitude, force.angle, scale);
        // Push the label past the arrow tip by half its own width along the arrow.
        const reach =
          LABEL_REACH +
          Math.abs(Math.cos(force.angle)) * force.label.length * 3.4;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed order
          <g key={index}>
            <line
              markerEnd={`url(#${arrowId(index)})`}
              stroke={CHART_COLORS[index % 4]}
              strokeWidth={2.5}
              x1={CENTRE}
              x2={end.x}
              y1={CENTRE}
              y2={end.y}
            />
            <text
              dominantBaseline="middle"
              fill={CHART_INK.text}
              fontSize={12}
              textAnchor="middle"
              x={clamp(
                label.x + reach * Math.cos(force.angle),
                LABEL_MARGIN,
                SIZE - LABEL_MARGIN
              )}
              y={clamp(label.y - reach * Math.sin(force.angle), 10, SIZE - 10)}
            >
              {force.label}
            </text>
          </g>
        );
      })}
      {net.angle === null ? null : (
        <g>
          <line
            markerEnd={`url(#${netId})`}
            stroke={CHART_COLORS[7]}
            strokeDasharray="6 4"
            strokeWidth={2.5}
            x1={CENTRE}
            x2={tip(net.magnitude, net.angle, scale).x}
            y1={CENTRE}
            y2={tip(net.magnitude, net.angle, scale).y}
          />
          <text
            fill={CHART_INK.text}
            fontSize={12}
            fontWeight={700}
            textAnchor="middle"
            x={
              tip(net.magnitude * 0.55, net.angle, scale).x +
              14 * Math.sin(net.angle)
            }
            y={
              tip(net.magnitude * 0.55, net.angle, scale).y +
              14 * Math.cos(net.angle)
            }
          >
            {labels.net}
          </text>
        </g>
      )}
      <rect
        fill={CHART_INK.background}
        height={BOX}
        stroke={CHART_INK.text}
        strokeWidth={2}
        width={BOX}
        x={CENTRE - BOX / 2}
        y={CENTRE - BOX / 2}
      />
    </svg>
  );
}
